import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, FileDown, Users } from "lucide-react";
import LeadMagnetDialog from "./LeadMagnetDialog";
import { supabase } from "@/integrations/supabase/client";
import { getAttributionUtms } from "@/lib/utmAttribution";
import { track } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface FormData {
  name: string;
  practiceName: string;
  email: string;
  phone: string;
  websiteUrl: string;
  monthlyCollections: string;
  currentNewPatients: string;
  targetNewPatients: string;
  biggestBottleneck: string;
  preferredTiming: string;
}

const initial: FormData = {
  name: "",
  practiceName: "",
  email: "",
  phone: "",
  websiteUrl: "",
  monthlyCollections: "",
  currentNewPatients: "",
  targetNewPatients: "",
  biggestBottleneck: "",
  preferredTiming: "",
};

type Errors = Partial<Record<keyof FormData, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+()\-.\s\d]{7,}$/; // permissive: digits + common separators, ≥7 chars

// Website validation: accepts "yourpractice.com", "www.yourpractice.com", or full URLs.
// Requires a real TLD (≥2 letters), no spaces, and rejects emails/single-label hosts.
const URL_EXAMPLE = "e.g. https://yourpractice.com";
const validateWebsite = (raw: string): string | undefined => {
  const v = raw.trim();
  if (!v) return "Please enter your practice website (e.g. https://yourpractice.com)";
  if (/\s/.test(v)) return `Remove spaces — ${URL_EXAMPLE}`;
  if (v.includes("@")) return `Looks like an email. Enter your website — ${URL_EXAMPLE}`;
  // Strip protocol & path so we can validate the host on its own
  const candidate = v.replace(/^https?:\/\//i, "").split("/")[0];
  if (!candidate) return `Enter a valid website — ${URL_EXAMPLE}`;
  // Host must contain a dot followed by a 2+ letter TLD
  const HOST_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
  if (!HOST_RE.test(candidate)) return `Enter a valid website — ${URL_EXAMPLE}`;
  // Final sanity check via URL constructor
  try {
    new URL(v.startsWith("http") ? v : `https://${v}`);
  } catch {
    return `Enter a valid website — ${URL_EXAMPLE}`;
  }
  return;
};

// Field labels used in error summaries (anchors users back to the right input)
const FIELD_LABELS: Record<keyof FormData, string> = {
  name: "First name",
  practiceName: "Practice name",
  email: "Email address",
  phone: "Mobile number",
  websiteUrl: "Practice website",
  monthlyCollections: "Monthly collections",
  currentNewPatients: "Current new patients / month",
  targetNewPatients: "Target new patients / month",
  biggestBottleneck: "Biggest growth bottleneck",
  preferredTiming: "Preferred timing",
};

const validateField = (field: keyof FormData, value: string, allValues?: FormData): string | undefined => {
  const v = (value ?? "").trim();
  switch (field) {
    case "name":
      if (!v) return "Add your first name so we know who to address your practice report to";
      if (v.length < 2) return "First name needs at least 2 characters";
      return;
    case "practiceName":
      if (!v) return "Add your practice name as it appears on your front door (e.g. Smile Dental)";
      if (v.length < 2) return "Practice name needs at least 2 characters";
      return;
    case "email":
      if (!v) return "Add the email where we should send your Growth Score + 90-day plan";
      if (!EMAIL_RE.test(v)) return "That email looks off — use the inbox you check daily (e.g. dr.smith@yourpractice.com)";
      return;
    case "phone":
      // optional — only validate if provided
      if (!v) return;
      if (!PHONE_RE.test(v)) return "Add a direct line we can reach you on (e.g. (555) 123-4567) — only used if we have a quick clarifying question";
      return;
    case "websiteUrl":
      return validateWebsite(v);
    case "monthlyCollections":
      if (!v) return "Pick the closest collections range — we benchmark your practice against similar dental offices";
      return;
    case "currentNewPatients":
      if (!v) return "Add how many new patients walk through your door each month";
      if (!/^\d+$/.test(v)) return "Use a whole number of new patients per month (e.g. 25)";
      return;
    case "targetNewPatients":
      if (!v) return "Add the monthly new-patient count your practice is aiming for";
      if (!/^\d+$/.test(v)) return "Use a whole number of new patients per month (e.g. 50)";
      return;
    case "biggestBottleneck":
      if (!v) return "In one sentence, what's slowing new-patient growth — phones, no-shows, ad spend, schedule gaps? It sharpens our analysis";
      if (v.length < 5) return "Add a little more detail about the bottleneck (at least 5 characters)";
      return;
    default:
      return;
  }
};

// Composes an aria-describedby string from a list of ids, dropping falsy entries.
// Used so inputs can point screen readers at their hint AND their inline error message.
const describedBy = (...ids: Array<string | false | null | undefined>) => {
  const filtered = ids.filter(Boolean) as string[];
  return filtered.length ? filtered.join(" ") : undefined;
};

// Respects the user's OS-level "Reduce motion" setting. Returns "auto" (instant)
// when reduced motion is preferred, "smooth" otherwise. Safe in SSR (returns "smooth").
const getScrollBehavior = (): ScrollBehavior => {
  if (typeof window === "undefined" || !window.matchMedia) return "smooth";
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
};

// ──────────────────────────────────────────────────────────────────────────────
// Screen-reader / a11y message catalog (Step 2)
// ──────────────────────────────────────────────────────────────────────────────
// Centralized strings spoken via the form's aria-live region. Kept as a single
// object with function-style entries so future localization (e.g. swapping for
// react-intl, i18next, or a per-locale dictionary) is a drop-in replacement —
// callers already pass structured args instead of formatting inline.
//
// To localize later: replace this object with a `useTranslation()`-backed
// lookup that returns the same shape, or move it to `src/i18n/checkup.ts`
// and key off the active locale. Call sites do not need to change.
const a11yMessages = {
  /** Spoken when the user advances from Step 1 → Step 2. */
  step2Entered: () =>
    "Step 2 of 2: Practice details. Tell us about your patient flow and growth goals to receive your Growth Score and 90-day plan.",

  /** Spoken when a Step 2 submit attempt is blocked by missing/invalid fields. */
  step2MissingFields: (fieldLabels: string[]) => {
    const list = fieldLabels.join(", ");
    const intro = fieldLabels.length === 1
      ? `1 field needs attention before we can send your Growth Score and 90-day plan: ${list}.`
      : `${fieldLabels.length} fields need attention before we can send your Growth Score and 90-day plan: ${list}.`;
    // Always end with a clear next action so SR users know exactly what to do next.
    return `${intro} Review the highlighted fields and resubmit.`;
  },

  /** Spoken when Step 2 validation passes and the form begins sending. */
  step2Sending: () =>
    "All fields look good. Sending your details — your Growth Score and 90-day plan will arrive in about 3 business days.",
} as const;

const Field = ({ id, label, required, optional, error, children }: { id?: string; label: string; required?: boolean; optional?: boolean; error?: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>
      {label}{required && " *"}
      {optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
    </Label>
    {children}
    {error && (
      <p id={id ? `${id}-error` : undefined} role="alert" className="text-xs text-destructive font-medium mt-1">{error}</p>
    )}
  </div>
);

const CheckupForm = () => {
  const [form, setForm] = useState<FormData>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [magnetOpen, setMagnetOpen] = useState(false);
  const [step2SubmitAttempted, setStep2SubmitAttempted] = useState(false);
  const [sendStatus, setSendStatus] = useState<"idle" | "sending" | "sent">("idle");
  // Persistent screen-reader-only live region message. Always-mounted so SRs reliably announce
  // changes (assertive live regions can be missed if they mount + populate in the same tick).
  const [a11yAnnouncement, setA11yAnnouncement] = useState("");
  const navigate = useNavigate();
  const focusedFields = useRef<Set<string>>(new Set());
  // Tracks every Step 2 field that ever blocked submission, so we can report which fixes
  // were required to reach a successful conversion.
  const step2BlockedHistory = useRef<Set<keyof FormData>>(new Set());
  const step2SubmitAttempts = useRef<number>(0);
  // Heading inside the validation summary — focused on each failed submit so keyboard
  // and screen-reader users land directly on the review banner.
  const validationSummaryHeadingRef = useRef<HTMLParagraphElement | null>(null);
  // Signature of the last set of missing fields announced to the live region.
  // Lets us skip re-announcing identical errors on repeat submits — avoiding
  // SR "spam" when the user re-clicks Submit without fixing anything.
  const lastAnnouncedMissingSig = useRef<string>("");
  // Success-screen heading — focused once the confirmation renders so keyboard/SR users
  // are moved out of the (now-unmounted) form and onto the "what happens next" content.
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const onFieldFocus = (field: keyof FormData) => {
    if (focusedFields.current.has(field)) return;
    focusedFields.current.add(field);
    try { sessionStorage.setItem("checkup_form_started", "1"); } catch { /* ignore */ }
    track("checkup_form_field_focused", { field, step });
  };

  const set = <K extends keyof FormData>(k: K, v: FormData[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    // Clear error as the user fixes it
    if (errors[k]) {
      const next = validateField(k, v as string);
      setErrors((p) => ({ ...p, [k]: next }));
    }
  };

  const onBlur = (k: keyof FormData) => {
    setTouched((p) => ({ ...p, [k]: true }));
    setErrors((p) => ({ ...p, [k]: validateField(k, form[k]) }));
  };

  const validateFields = (fields: (keyof FormData)[]) => {
    const next: Errors = {};
    fields.forEach((f) => {
      const msg = validateField(f, form[f], form);
      if (msg) next[f] = msg;
    });
    setErrors((p) => ({ ...p, ...next }));
    setTouched((p) => ({ ...p, ...Object.fromEntries(fields.map((f) => [f, true])) }));
    return Object.keys(next).length === 0;
  };

  // Foot-in-the-door: Step 1 asks only for name + email (lowest friction, captures a lead).
  // Website moved to Step 2 so we get a contactable lead even if they abandon.
  const step1Fields: (keyof FormData)[] = ["name", "email"];
  const step2Fields: (keyof FormData)[] = ["practiceName", "websiteUrl", "phone", "monthlyCollections", "currentNewPatients", "targetNewPatients", "biggestBottleneck"];

  // Resolve UTM attribution: URL first, then stored first-touch
  const utmParams = getAttributionUtms();

  // Fire `growth_checkup_step2_viewed` exactly once when user first lands on step 2.
  const step2ViewedRef = useRef(false);
  useEffect(() => {
    if (step !== 2 || step2ViewedRef.current) return;
    step2ViewedRef.current = true;
    if (typeof window !== "undefined" && (window as any).posthog) {
      (window as any).posthog.capture("growth_checkup_step2_viewed", {
        website_url: form.websiteUrl,
        ...utmParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // After Step 2 submits successfully and the confirmation screen mounts, move keyboard
  // focus to the success heading so users aren't stranded on the now-removed form.
  // Two rAFs ensure the new DOM is committed and the heading node is mounted before focusing.
  useEffect(() => {
    if (!submitted) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        successHeadingRef.current?.focus({ preventScroll: false });
        successHeadingRef.current?.scrollIntoView({ behavior: getScrollBehavior(), block: "center" });
      });
    });
  }, [submitted]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep2SubmitAttempted(true);
    step2SubmitAttempts.current += 1;

    // Click-time event — fires for EVERY Step 2 button click (valid or not).
    // Use this as the denominator for "Step 2 click → submit" conversion.
    const missingFields = step2Fields.filter((f) => !!validateField(f, form[f], form));

    // Record any blocking fields into the cumulative history so we can later report
    // which fields had to be corrected before the user successfully submitted.
    missingFields.forEach((f) => step2BlockedHistory.current.add(f));

    track("checkup_form_step2_cta_clicked", {
      step: 2,
      attempt: step2SubmitAttempts.current,
      label: "Get My Growth Score + 90-Day Plan",
      has_validation_errors: missingFields.length > 0,
      missing_field_count: missingFields.length,
      missing_fields: missingFields,
    });

    if (!validateFields(step2Fields)) {
      // Dedicated blocked-submission event with the exact missing fields & their reasons.
      // Use this in PostHog as the "Step 2 friction" funnel step to see which fields most
      // often block submission (denominator: checkup_form_step2_cta_clicked).
      const blockedReasons = missingFields.reduce<Record<string, string>>((acc, f) => {
        const msg = validateField(f, form[f], form);
        if (msg) acc[f] = msg;
        return acc;
      }, {});
      track("checkup_form_step2_submission_blocked", {
        step: 2,
        attempt: step2SubmitAttempts.current,
        missing_fields: missingFields,
        missing_field_count: missingFields.length,
        blocked_reasons: blockedReasons,
        cumulative_blocked_fields: Array.from(step2BlockedHistory.current),
        ...utmParams,
      });

      // Screen-reader announcement — names every blocked field so AT users hear what to fix
      // immediately on submit, without having to navigate to find the banner.
      // Stable signature: sorted field keys. If unchanged from the last announcement,
      // skip updating the live region so SR users aren't spammed with the same message
      // when they resubmit without fixing anything.
      const missingSig = [...missingFields].sort().join("|");
      if (missingSig !== lastAnnouncedMissingSig.current) {
        lastAnnouncedMissingSig.current = missingSig;
        const fieldNamesForSr = missingFields.map((f) => FIELD_LABELS[f]);
        const srMessage = a11yMessages.step2MissingFields(fieldNamesForSr);
        // Reset first so a genuinely new set of errors still re-triggers the announcement reliably.
        setA11yAnnouncement("");
        requestAnimationFrame(() => setA11yAnnouncement(srMessage));
      }

      // Move keyboard focus to the validation summary heading once it renders, so keyboard
      // users get a quick review of what's blocking submission without hunting for the banner.
      // Two rAFs: first lets React commit the summary, second ensures the node is mounted.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          validationSummaryHeadingRef.current?.focus({ preventScroll: false });
          validationSummaryHeadingRef.current?.scrollIntoView({ behavior: getScrollBehavior(), block: "center" });
        });
      });

      // Auto-scroll + focus the FIRST blocked field so the user lands directly on what to fix
      // (banner appears just above; this skips them past it to the input).
      const fieldIds: Record<keyof FormData, string> = {
        name: "ck-name",
        websiteUrl: "ck-website",
        practiceName: "ck-practice",
        email: "ck-email",
        phone: "ck-phone",
        monthlyCollections: "ck-collections",
        currentNewPatients: "ck-current",
        targetNewPatients: "ck-target",
        biggestBottleneck: "ck-bottleneck",
        preferredTiming: "ck-timing",
      };
      const firstBlocked = step2Fields.find((f) => !!validateField(f, form[f], form));
      if (firstBlocked) {
        // Defer to next frame so the banner has rendered and layout is stable.
        requestAnimationFrame(() => {
          const el = document.getElementById(fieldIds[firstBlocked]);
          if (!el) return;
          const behavior = getScrollBehavior();
          el.scrollIntoView({ behavior, block: "center" });
          // Skip the deferred-focus delay when reduced motion is on (no smooth scroll to wait for).
          const focusDelay = behavior === "smooth" ? 250 : 0;
          setTimeout(() => (el as HTMLElement).focus({ preventScroll: true }), focusDelay);
          track("checkup_form_first_blocked_field_scrolled", { field: firstBlocked, step: 2 });
        });
      }
      return;
    }
    setSubmitting(true);

    // Validation passed — clear any prior blocked-field announcement and confirm send to SR users.
    // Reset the announcement signature so a future failed submit (e.g. retry after server error)
    // will re-announce the missing fields fresh.
    lastAnnouncedMissingSig.current = "";
    setA11yAnnouncement("");
    requestAnimationFrame(() => setA11yAnnouncement(a11yMessages.step2Sending()));

    // longer in the current missing set was corrected before this successful submission.
    const originallyBlocked = Array.from(step2BlockedHistory.current);
    const correctedFields = originallyBlocked; // by definition: in history, but not currently missing

    // Step 2 completed (validation passed) — fired before final submit so funnel can isolate
    // "filled out step 2 fields" vs. "submission processed".
    if (typeof window !== "undefined" && (window as any).posthog) {
      (window as any).posthog.capture("growth_checkup_step2_completed", {
        practice_name: form.practiceName,
        monthly_collections: form.monthlyCollections,
        current_new_patients: form.currentNewPatients,
        target_new_patients: form.targetNewPatients,
        // Conversion-friction signals — measure which corrections drove the successful submit
        submit_attempts: step2SubmitAttempts.current,
        had_validation_friction: originallyBlocked.length > 0,
        originally_blocked_fields: originallyBlocked,
        originally_blocked_count: originallyBlocked.length,
        corrected_fields: correctedFields,
        corrected_count: correctedFields.length,
        ...utmParams,
      });
    }

    // PostHog conversion event with UTM attribution — send_instantly to flush before navigate()
    if (typeof window !== "undefined" && (window as any).posthog) {
      (window as any).posthog.capture(
        "growth_checkup_submitted",
        {
          practice_name: form.practiceName,
          monthly_collections: form.monthlyCollections,
          current_new_patients: form.currentNewPatients,
          target_new_patients: form.targetNewPatients,
          ...utmParams,
        },
        { send_instantly: true }
      );
    }

    // Fire webhook in background — don't block navigation
    supabase.functions.invoke("ghl-webhook", {
      body: {
        ...form,
        ...utmParams,
        source: "growth-checkup",
      },
    }).catch((err) => console.error("Webhook error:", err));

    // Inline "sending" state appears in the button row immediately so the user gets feedback.
    setSendStatus("sending");

    // Quick "sent" beat (~700ms) before swapping to the full confirmation screen — gives a
    // satisfying tactile confirmation without delaying the redirect noticeably.
    setTimeout(() => {
      setSendStatus("sent");
      // Show full inline confirmation screen
      setSubmitted(true);
      setSubmitting(false);

      // Hold the confirmation visible long enough to read the "what happens next" timeline,
      // then redirect to the dedicated thank-you page.
      setTimeout(() => {
        navigate("/GetGrowthScore/thank-you", {
          state: { name: form.name, email: form.email, utmParams },
        });
      }, 3500);
    }, 700);
  };

  return (
    <section id="checkup-form" className="py-14 md:py-20 bg-background">
      <div className="max-w-xl mx-auto section-padding">
        {submitted ? (
          <div
            role="status"
            aria-live="polite"
            className="bg-card rounded-2xl p-8 md:p-10 border border-accent/40 shadow-lg text-center animate-in fade-in zoom-in-95 duration-300"
          >
            <div className="w-16 h-16 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9 text-accent" />
            </div>
            <h2
              ref={successHeadingRef}
              tabIndex={-1}
              className="text-2xl md:text-3xl font-bold text-foreground tracking-tight mb-3 outline-none focus-visible:ring-2 focus-visible:ring-accent/40 rounded"
            >
              You're all set{form.name ? `, ${form.name.split(" ")[0]}` : ""} 🎉
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-5 text-base">
              We'll review {form.websiteUrl ? <span className="font-semibold text-foreground">{form.websiteUrl}</span> : "your practice"} and send your{" "}
              <span className="font-semibold text-accent">Growth Score + 90-day plan</span> within{" "}
              <span className="font-semibold text-foreground">~3 business days</span> to{" "}
              {form.email ? <span className="font-semibold text-foreground">{form.email}</span> : "your inbox"}.
            </p>

            {/* What happens next — 3-step timeline so the user knows the exact path to their report */}
            <div className="bg-muted/40 rounded-xl p-4 md:p-5 mb-5 text-left">
              <p className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mb-3">
                What happens next
              </p>
              <ol className="relative space-y-4 ml-1">
                {[
                  {
                    when: "Today",
                    title: "Submission received",
                    body: "We've got your details. No call, no pitch — just analysis.",
                    done: true,
                  },
                  {
                    when: "Days 1–2",
                    title: "We review your practice",
                    body: "Our team digs into your website, ads, tracking, and new-patient flow.",
                    done: false,
                  },
                  {
                    when: "By day 3",
                    title: "Growth Score + 90-day plan delivered",
                    body: `Sent to ${form.email ? form.email : "your inbox"} — yours to keep, no obligation.`,
                    done: false,
                  },
                ].map((s, i, arr) => (
                  <li key={s.title} className="flex gap-3 relative">
                    {/* Connector line */}
                    {i < arr.length - 1 && (
                      <span className="absolute left-[11px] top-6 bottom-[-1rem] w-px bg-border" aria-hidden="true" />
                    )}
                    <div
                      className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        s.done
                          ? "bg-accent text-accent-foreground"
                          : "bg-background border border-border text-muted-foreground"
                      }`}
                    >
                      {s.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <div className="flex-1 pb-1">
                      <p className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                        {s.when}
                      </p>
                      <p className="text-sm font-semibold text-foreground leading-snug">{s.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading your confirmation page…
            </div>
          </div>
        ) : (
          <>
        {/* Trust strip near form */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-4 text-xs text-muted-foreground">
          <span>✓ No obligation</span>
          <span>✓ Delivered in ~3 business days</span>
          <span>✓ Built by a dental-focused team</span>
        </div>

        <div className="text-center mb-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight mb-2">
            Request Your New Patient Growth Checkup
          </h2>
          <p className="text-muted-foreground text-sm">
            Tell us a little about your practice so we can review the right things.
          </p>
        </div>

        {/* Micro-proof — social proof at point of conversion (E) */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/60 border border-border rounded-full px-3 py-1.5">
            <Users className="w-3.5 h-3.5 text-accent" />
            <span>Hundreds of dental practices scored &amp; reviewed</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg">
          {/* Persistent visually-hidden live region — always mounted so screen readers reliably
              announce validation status (assertive). Populated on every submit attempt. */}
          <div role="status" aria-live="assertive" aria-atomic="true" className="sr-only">
            {a11yAnnouncement}
          </div>
          {/* Step progress indicator */}
          {(() => {
            const steps = [
              { n: 1, label: "Your basics", hint: "~15 sec" },
              { n: 2, label: "Practice details", hint: "~60 sec" },
            ];
            const progressPct = step === 1 ? 50 : 100;
            return (
              <div className="mb-8" role="group" aria-label="Form progress">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                    Step {step} of {steps.length}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {steps[step - 1].hint}
                  </span>
                </div>
                {/* Progress bar */}
                <div
                  className="relative h-1.5 bg-muted rounded-full overflow-hidden mb-4"
                  role="progressbar"
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-accent rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {/* Labelled step nodes */}
                <div className="flex items-center justify-between">
                  {steps.map((s) => {
                    const active = step === s.n;
                    const done = step > s.n;
                    return (
                      <div key={s.n} className="flex items-center gap-2">
                        <div
                          className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                            done
                              ? "bg-accent text-accent-foreground"
                              : active
                              ? "bg-accent text-accent-foreground ring-4 ring-accent/20"
                              : "bg-muted text-muted-foreground"
                          }`}
                          aria-current={active ? "step" : undefined}
                        >
                          {done ? <CheckCircle2 className="w-4 h-4" /> : s.n}
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            active || done ? "text-foreground" : "text-muted-foreground"
                          }`}
                        >
                          {s.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {step === 1 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Start your checkup</p>
              <p className="text-sm text-muted-foreground -mt-3">Two quick details — takes ~15 seconds.</p>

              <Field id="ck-name" label="Your First Name" required error={touched.name ? errors.name : undefined}>
                <Input id="ck-name" autoComplete="given-name" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => onBlur("name")} onFocus={() => onFieldFocus("name")} placeholder="First name" aria-invalid={!!(touched.name && errors.name)} aria-describedby={describedBy(touched.name && errors.name && "ck-name-error")} className={touched.name && errors.name ? "border-destructive focus-visible:ring-destructive" : ""} />
              </Field>

              <Field id="ck-email" label="Email Address" required error={touched.email ? errors.email : undefined}>
                <Input id="ck-email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={() => onBlur("email")} onFocus={() => onFieldFocus("email")} placeholder="you@practice.com" aria-invalid={!!(touched.email && errors.email)} aria-describedby={describedBy(touched.email && errors.email && "ck-email-error")} className={touched.email && errors.email ? "border-destructive focus-visible:ring-destructive" : ""} />
              </Field>

              <button
                type="button"
                onClick={() => {
                  if (validateFields(step1Fields)) {
                    if ((window as any).posthog) {
                      (window as any).posthog.capture("growth_checkup_step1_completed", {
                        email: form.email,
                        ...utmParams,
                      });
                    }
                    setStep(2);
                    setA11yAnnouncement("");
                    requestAnimationFrame(() => setA11yAnnouncement(a11yMessages.step2Entered()));
                  } else {
                    track("checkup_form_validation_blocked", { step: 1 });
                  }
                }}
                className="w-full bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
              >
                Continue →
              </button>

              <p className="text-xs text-muted-foreground/70 text-center">
                No call · No pitch · Delivered in ~3 business days
              </p>
            </div>
          )}

          {step === 2 && (() => {
            // Live completion tracker — recomputes on every render as the user fills/fixes fields.
            // Phone is optional, so it's excluded from the "required" denominator.
            const requiredStep2: (keyof FormData)[] = [
              "practiceName",
              "websiteUrl",
              "monthlyCollections",
              "currentNewPatients",
              "targetNewPatients",
              "biggestBottleneck",
            ];
            const completed = requiredStep2.filter((f) => !validateField(f, form[f], form)).length;
            const total = requiredStep2.length;
            const pct = Math.round((completed / total) * 100);
            const isReady = completed === total;

            return (
            <div className="space-y-5">
              <div>
                <p className="font-semibold text-foreground text-lg">A few practice details so we review the right things</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tell us about your patient flow, schedule, and growth goals. Then we'll send your <span className="font-semibold text-foreground">Growth Score + 90-day plan</span> in <span className="font-semibold text-foreground">~3 business days</span>.
                </p>
              </div>

              {/* Live completion state — flips to "ready" once all required Step 2 fields validate */}
              <div
                role="status"
                aria-live="polite"
                className={`rounded-lg border p-3 transition-colors ${
                  isReady
                    ? "border-accent/40 bg-accent/10"
                    : "border-border bg-muted/40"
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 text-sm">
                    {isReady ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                        <span className="font-semibold text-foreground">
                          Ready to submit — we'll send your Growth Score + 90-day plan
                        </span>
                      </>
                    ) : (
                      <span className="font-medium text-foreground/80">
                        {completed} of {total} required fields complete
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-semibold tabular-nums ${isReady ? "text-accent" : "text-muted-foreground"}`}>
                    {pct}%
                  </span>
                </div>
                <div className="h-1.5 bg-background rounded-full overflow-hidden" aria-hidden="true">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ease-out ${isReady ? "bg-accent" : "bg-foreground/30"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>


              <Field id="ck-practice" label="Practice Name" required error={touched.practiceName ? errors.practiceName : undefined}>
                <Input id="ck-practice" value={form.practiceName} onChange={(e) => set("practiceName", e.target.value)} onBlur={() => onBlur("practiceName")} onFocus={() => onFieldFocus("practiceName")} aria-invalid={!!(touched.practiceName && errors.practiceName)} aria-describedby={describedBy(touched.practiceName && errors.practiceName && "ck-practice-error")} className={touched.practiceName && errors.practiceName ? "border-destructive focus-visible:ring-destructive" : ""} />
              </Field>

              <Field id="ck-website" label="Practice Website" required error={touched.websiteUrl ? errors.websiteUrl : undefined}>
                <Input id="ck-website" type="url" inputMode="url" autoComplete="url" value={form.websiteUrl} onChange={(e) => set("websiteUrl", e.target.value)} onBlur={() => onBlur("websiteUrl")} onFocus={() => onFieldFocus("websiteUrl")} placeholder="https://yourpractice.com" aria-describedby={describedBy(!(touched.websiteUrl && errors.websiteUrl) && "ck-website-hint", touched.websiteUrl && errors.websiteUrl && "ck-website-error")} aria-invalid={!!(touched.websiteUrl && errors.websiteUrl)} className={touched.websiteUrl && errors.websiteUrl ? "border-destructive focus-visible:ring-destructive" : ""} />
                {!(touched.websiteUrl && errors.websiteUrl) && (
                  <p id="ck-website-hint" className="text-xs text-muted-foreground mt-1">
                    Example: <span className="font-mono">https://yourpractice.com</span>
                  </p>
                )}
              </Field>

              <Field id="ck-phone" label="Mobile Number" optional error={touched.phone ? errors.phone : undefined}>
                <Input id="ck-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} onBlur={() => onBlur("phone")} onFocus={() => onFieldFocus("phone")} aria-describedby={describedBy(touched.phone && errors.phone && "ck-phone-hint", touched.phone && errors.phone && "ck-phone-error")} aria-invalid={!!(touched.phone && errors.phone)} className={touched.phone && errors.phone ? "border-destructive focus-visible:ring-destructive" : ""} />
                {touched.phone && errors.phone && (
                  <p id="ck-phone-hint" className="text-xs text-muted-foreground mt-1">
                    Format: <span className="font-mono text-foreground/80">(555) 123-4567</span> — digits and ()-. allowed
                  </p>
                )}
              </Field>

              <Field id="ck-collections" label="Approximate Monthly Collections" required error={touched.monthlyCollections ? errors.monthlyCollections : undefined}>
                <Select value={form.monthlyCollections} onValueChange={(v) => { onFieldFocus("monthlyCollections"); set("monthlyCollections", v); setTouched((p) => ({ ...p, monthlyCollections: true })); }}>
                  <SelectTrigger id="ck-collections" aria-describedby={describedBy(touched.monthlyCollections && errors.monthlyCollections && "ck-collections-error")} aria-invalid={!!(touched.monthlyCollections && errors.monthlyCollections)} className={touched.monthlyCollections && errors.monthlyCollections ? "border-destructive focus:ring-destructive" : ""}><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-80k">Under $80K</SelectItem>
                    <SelectItem value="80k-100k">$80K – $100K</SelectItem>
                    <SelectItem value="100k-150k">$100K – $150K</SelectItem>
                    <SelectItem value="150k-250k">$150K – $250K</SelectItem>
                    <SelectItem value="250k-plus">$250K+</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="ck-current" label="Current New Patients / Month" required error={touched.currentNewPatients ? errors.currentNewPatients : undefined}>
                  <Input id="ck-current" inputMode="numeric" value={form.currentNewPatients} onChange={(e) => set("currentNewPatients", e.target.value)} onBlur={() => onBlur("currentNewPatients")} onFocus={() => onFieldFocus("currentNewPatients")} placeholder="e.g. 25" aria-invalid={!!(touched.currentNewPatients && errors.currentNewPatients)} aria-describedby={describedBy(touched.currentNewPatients && errors.currentNewPatients && "ck-current-error")} className={touched.currentNewPatients && errors.currentNewPatients ? "border-destructive focus-visible:ring-destructive" : ""} />
                </Field>
                <Field id="ck-target" label="Target New Patients / Month" required error={touched.targetNewPatients ? errors.targetNewPatients : undefined}>
                  <Input id="ck-target" inputMode="numeric" value={form.targetNewPatients} onChange={(e) => set("targetNewPatients", e.target.value)} onBlur={() => onBlur("targetNewPatients")} onFocus={() => onFieldFocus("targetNewPatients")} placeholder="e.g. 50" aria-invalid={!!(touched.targetNewPatients && errors.targetNewPatients)} aria-describedby={describedBy(touched.targetNewPatients && errors.targetNewPatients && "ck-target-error")} className={touched.targetNewPatients && errors.targetNewPatients ? "border-destructive focus-visible:ring-destructive" : ""} />
                </Field>
              </div>

              <Field id="ck-bottleneck" label="Biggest Growth Bottleneck" required error={touched.biggestBottleneck ? errors.biggestBottleneck : undefined}>
                <Textarea id="ck-bottleneck" value={form.biggestBottleneck} onChange={(e) => set("biggestBottleneck", e.target.value)} onBlur={() => onBlur("biggestBottleneck")} onFocus={() => onFieldFocus("biggestBottleneck")} placeholder="e.g. phones missing new-patient calls, too many no-shows, ad spend not booking appointments, hygiene schedule has gaps…" rows={2} aria-invalid={!!(touched.biggestBottleneck && errors.biggestBottleneck)} aria-describedby={describedBy(touched.biggestBottleneck && errors.biggestBottleneck && "ck-bottleneck-error")} className={touched.biggestBottleneck && errors.biggestBottleneck ? "border-destructive focus-visible:ring-destructive" : ""} />
              </Field>

              <Field id="ck-timing" label="Best time to reach you between patients">
                <Input id="ck-timing" value={form.preferredTiming} onChange={(e) => set("preferredTiming", e.target.value)} onFocus={() => onFieldFocus("preferredTiming")} placeholder="Optional — e.g. before huddle, lunch break, after last patient" />
              </Field>

              {/* Validation summary — only after the user attempts to submit Step 2, so passive
                  field touches don't surface the banner and clutter the promise/page. */}
              {(() => {
                if (!step2SubmitAttempted) return null;
                const blockedFields = step2Fields.filter((f) => errors[f]);
                if (blockedFields.length === 0) return null;
                return (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm"
                  >
                    <p
                      ref={validationSummaryHeadingRef}
                      tabIndex={-1}
                      className="font-semibold text-destructive mb-1 outline-none focus-visible:ring-2 focus-visible:ring-destructive/40 rounded"
                    >
                      {blockedFields.length === 1 ? "1 field needs attention" : `${blockedFields.length} fields need attention`}
                    </p>
                    <ul className="space-y-1 text-foreground/80 mb-2">
                      {blockedFields.map((f) => {
                        const fieldIds: Record<keyof FormData, string> = {
                          name: "ck-name",
                          websiteUrl: "ck-website",
                          practiceName: "ck-practice",
                          email: "ck-email",
                          phone: "ck-phone",
                          monthlyCollections: "ck-collections",
                          currentNewPatients: "ck-current",
                          targetNewPatients: "ck-target",
                          biggestBottleneck: "ck-bottleneck",
                          preferredTiming: "ck-timing",
                        };
                        const targetId = fieldIds[f];
                        const handleJump = (e: React.MouseEvent) => {
                          e.preventDefault();
                          const el = document.getElementById(targetId);
                          if (!el) return;
                          const behavior = getScrollBehavior();
                          el.scrollIntoView({ behavior, block: "center" });
                          // Defer focus until after smooth scroll starts so the input doesn't pull viewport.
                          // With reduced motion (instant scroll), focus immediately — no wait needed.
                          const focusDelay = behavior === "smooth" ? 250 : 0;
                          setTimeout(() => {
                            (el as HTMLElement).focus({ preventScroll: true });
                          }, focusDelay);
                          track("checkup_form_error_jump_clicked", { field: f, step: 2 });
                        };
                        const errorDescId = `${targetId}-summary-error`;
                        return (
                          <li key={f} className="flex flex-wrap items-baseline gap-x-2">
                            <a
                              href={`#${targetId}`}
                              onClick={handleJump}
                              aria-label={`Go to ${FIELD_LABELS[f]} field to fix the error`}
                              aria-describedby={errorDescId}
                              className="font-medium text-destructive underline underline-offset-2 hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40 rounded"
                            >
                              Fix {FIELD_LABELS[f]}
                              <span aria-hidden="true"> →</span>
                            </a>
                            <span id={errorDescId} className="text-xs text-muted-foreground">{errors[f]}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-xs text-muted-foreground">
                      Fix these and you'll get your <span className="font-semibold text-foreground">Growth Score + 90-day plan</span> in <span className="font-semibold text-foreground">~3 business days</span>.
                    </p>
                  </div>
                );
              })()}

              {/* Trust/guarantee badge — sits directly above the CTA to reduce hesitation at the click moment */}
              <div className="flex justify-center">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[11px] font-semibold text-accent"
                  aria-label="Our promise: no call, no pitch, no obligation"
                >
                  <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                  <span>No call. No pitch. No obligation.</span>
                </div>
              </div>

              {/* Submit row — submit appears first on mobile (stacked) and right on desktop (side-by-side) */}
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="sm:w-auto px-5 py-3 rounded-lg font-medium border border-border text-foreground hover:bg-muted transition-all inline-flex items-center justify-center"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" />Back
                </button>
                <button
                  type="submit"
                  disabled={submitting || sendStatus !== "idle"}
                  className="flex-1 bg-accent text-accent-foreground px-3 py-3.5 rounded-lg font-semibold text-[14px] sm:text-base leading-tight hover:bg-accent/90 transition-all inline-flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-60 shadow-lg shadow-accent/20 text-center"
                >
                  {sendStatus === "sending" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending your details…</>
                  ) : sendStatus === "sent" ? (
                    <><CheckCircle2 className="w-4 h-4" /> Sent — preparing your confirmation</>
                  ) : submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    <><span>Get My Growth Score + 90-Day Plan</span> <ArrowRight className="w-4 h-4 shrink-0" /></>
                  )}
                </button>
              </div>
              <p className="text-center text-xs text-muted-foreground mt-2">
                No call · No pitch · Delivered in ~3 business days
              </p>
            </div>
            );
          })()}
        </form>

        <p className="text-muted-foreground/50 text-xs text-center mt-4">
          We use this information to prepare a more useful review of your practice. We do not ask for anything we do not need.
        </p>

        {/* Lead magnet alternative — prominent lower-friction capture path (F) */}
        <div className="mt-8 bg-card rounded-2xl p-5 md:p-6 border border-border shadow text-center">
          <p className="text-xs font-bold tracking-wider uppercase text-accent mb-2">Not ready for the full checkup?</p>
          <p className="text-sm text-foreground font-semibold mb-1">
            Grab the New Patient Leak Checklist (free PDF)
          </p>
          <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
            The 7-point self-audit we run on $1M+ practices. Find the 2 biggest leaks in your front-desk → consult → close flow in under 10 minutes.
          </p>
          <button
            onClick={() => {
              track("form_area_lead_magnet_clicked", { magnet: "leak-checklist", source: "below-form" });
              setMagnetOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 bg-foreground/10 hover:bg-foreground/15 border border-border hover:border-foreground/20 text-foreground px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <FileDown className="w-4 h-4" />
            Download Free Checklist
          </button>
        </div>
        <LeadMagnetDialog open={magnetOpen} onOpenChange={setMagnetOpen} redirectTo="/quick-score" source="below-form" />
          </>
        )}
      </div>
    </section>
  );
};

export default CheckupForm;
